import { ReactElement, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { projectPointOntoPlane } from '@editorUtils'
import { circularShiftRightShape } from '@geometry'
import {
  cloneDeep,
  first,
  inRange,
  isNull,
  last,
  reject,
  reverse,
  sortBy,
  toNumber,
} from 'lodash-es'
import { Plane } from 'three'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import {
  Button,
  FormGroup,
  MenuItem,
  Stack,
  TextField as MUITextField,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { SwitchLabeled } from '@ui/actions'
import { CoordinatePointInput, Errors, Label, TextField } from '@ui/forms'
import { roofStoreyKey } from '@editorStores'
import { useStoreSubscription } from '@editorHooks'

type VerticalSlabGeometryType = 'rectangular' | 'trapezoidal'

interface Props {
  updateShape: (shape: { guid: string; shape: PolygonShape }) => void
  updateStorey: (guid: string, storey: string) => void
  storeys: string[]
  disableStoreySelection?: boolean
  geometryType?: VerticalSlabGeometryType
  setGeometryType?: (type: VerticalSlabGeometryType) => void
  elementType?: string
  showCreateLabel?: boolean
}

const FormFields = ({
  updateShape,
  updateStorey,
  storeys,
  disableStoreySelection = false,
  geometryType = 'rectangular',
  setGeometryType,
  elementType,
  showCreateLabel = false,
}: Props): ReactElement => {
  const { watch, setValue } = useFormContext()
  const guid = watch('guid')

  const shape: PolygonShape = watch('shape')
  const points: Point[] = shape.points
  const switchXY: boolean = useWatch({ name: 'xy_decimal_points_rounding' })
  const automaticZ = useWatch({ name: 'automatic_z_calculation' })

  const orderedStoreys = useMemo(() => {
    const nonRoofStoreys = reverse(
      sortBy(
        reject(storeys, storey => storey === roofStoreyKey),
        storey => toNumber(storey),
      ),
    )
    return storeys.includes(roofStoreyKey) ? [roofStoreyKey, ...nonRoofStoreys] : nonRoofStoreys
  }, [storeys])

  useStoreSubscription({
    compareValues: true,
    fieldName: 'shape',
    writeCallback: shape => {
      const roofPoints = shape.points.map(
        ({ x, y, z }: { x: number; y: number; z: number }) =>
          new ImmutableVector3(toNumber(x), toNumber(y), toNumber(z)),
      )

      updateShape({
        guid,
        shape: {
          points: roofPoints,
          shape_type: shape.shape_type,
        },
      })
    },
  })

  useStoreSubscription({
    compareValues: true,
    fieldName: 'storey',
    writeCallback: (storey: string) => {
      updateStorey(guid, storey)
    },
  })

  const onClickShiftPointsOneRight = () => {
    updateShape({ guid, shape: circularShiftRightShape(shape, 1) })
  }
  const onClickShiftPointsOneLeft = () => {
    updateShape({ guid, shape: circularShiftRightShape(shape, -1) })
  }
  const disableOrientationSelection = shape.shape_type && shape.shape_type !== 'Rectangular'
  const allowCircularShift = true

  const getPlaneForZCalculation = (index: number) => {
    const pointsForRoofSlab = reject(points, (p, i) => i === index)

    return new Plane().setFromCoplanarPoints(
      pointsForRoofSlab[0].v,
      pointsForRoofSlab[1].v,
      pointsForRoofSlab[2].v,
    )
  }

  const change = (
    index: number,
    field: 'x' | 'y',
  ): ((value: number | null) => void) | undefined => {
    if (!automaticZ) return undefined

    return (value: number | null) => {
      if (isNull(value)) return
      const newPoints = cloneDeep(points)
      const changedPoint = points[index]
      const plane = getPlaneForZCalculation(index)

      const newPoint = new ImmutableVector3(
        field === 'x' ? value : changedPoint.x,
        field === 'y' ? value : changedPoint.y,
        changedPoint.z,
      )
      // this is required to avoid numerical inaccuracies when all points have same z
      const allPointsHaveSameZ = points.every(p => p.z === changedPoint.z)
      const newZ = allPointsHaveSameZ
        ? changedPoint.z
        : (projectPointOntoPlane(plane, newPoint) as ImmutableVector3).z
      const finalPoint = new ImmutableVector3(newPoint.x, newPoint.y, newZ)
      newPoints[index] = finalPoint
      setValue('shape.points', newPoints, { shouldTouch: true })
    }
  }

  const onReplace = (index: number) => {
    return (x: number, y: number, z: number) => {
      const newPoints = cloneDeep(points)

      let newPoint: ImmutableVector3

      if (automaticZ) {
        const plane = getPlaneForZCalculation(index)
        const allPointsHaveSameZ = points.every(p => p.z === points[index].z)
        // this is required to avoid numerical inaccuracies when all points have same z
        if (allPointsHaveSameZ) {
          const newZ = points[index].z
          newPoint = new ImmutableVector3(x, y, newZ)
        } else {
          newPoint = projectPointOntoPlane(plane, new ImmutableVector3(x, y, z))
        }

        newPoints[index] = newPoint
      } else {
        newPoint = new ImmutableVector3(x, y, z)

        setValue('shape.points', newPoints, { shouldTouch: true })
      }

      newPoints[index] = newPoint
      setValue('shape.points', newPoints, { shouldTouch: true })
    }
  }

  const orientation = useMemo(() => {
    const p1 = points[0]
    const p2 = points[1]
    const direction = p2.sub(p1).normalize()
    const orientation = inRange(direction.x, -0.01, 0.01) ? 'horizontal' : 'vertical'

    return orientation
  }, [points])

  const updateOrientationInternal = useCallback(
    (orientation: 'horizontal' | 'vertical') => {
      const isHorizontal = orientation === 'horizontal'
      const firstPoint = first(points)
      const lastPoint = last(points)
      const newPoints = isHorizontal
        ? [lastPoint, ...points.slice(0, 3)]
        : [...points.slice(1, 4), firstPoint]

      setValue('shape.points', newPoints, { shouldTouch: true })
    },
    [points, setValue],
  )

  // Only show geometry type toggle for vertical slabs and vertical roof slabs with 4 points
  const showGeometryTypeToggle =
    points.length === 4 &&
    setGeometryType &&
    (elementType === 'vertical_slabs' || elementType === 'vertical_roof_slabs')

  return (
    <>
      {showCreateLabel && <CreateElementLabelField />}
      {showGeometryTypeToggle && (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              value={geometryType}
              exclusive
              onChange={(_, newValue) => newValue && setGeometryType(newValue)}
              aria-label="vertical slab geometry type"
              fullWidth={true}
            >
              <ToggleButton
                value="rectangular"
                aria-label="rectangular vertical slab"
                fullWidth={true}
                sx={{ paddingY: 0 }}
              >
                <Typography>Rechteckig</Typography>
              </ToggleButton>
              <ToggleButton
                value="trapezoidal"
                aria-label="trapezoidal vertical slab"
                fullWidth={true}
                sx={{ paddingY: 0 }}
              >
                <Typography>Trapezförmig</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {geometryType === 'rectangular'
              ? 'Rechteckige Position: benachbarte Punkte müssen entweder gleiche x oder gleiche y Koordinaten haben.'
              : 'Trapezförmige Position: Diagonale Kante muss Kante Nr. 3 sein.'}
          </Typography>
        </>
      )}

      {points.map((point, index) => {
        return (
          <CoordinatePointInput
            key={index}
            label={`P${index + 1}`}
            name={`shape.points[${index}]`}
            pointManipulation={{
              x: { roundOnReplace: switchXY },
              y: { roundOnReplace: switchXY },
              z: { disabled: automaticZ },
            }}
            onChangeX={change(index, 'x')}
            onChangeY={change(index, 'y')}
            onReplace={onReplace(index)}
          />
        )
      })}
      {allowCircularShift && (
        <Stack direction="row" spacing={1}>
          <Button variant={'outlined'} onClick={onClickShiftPointsOneRight} size="small" fullWidth>
            <ArrowDownwardIcon sx={{ mr: 1 }} />
            vorw. verschieben
          </Button>
          <Button variant={'outlined'} onClick={onClickShiftPointsOneLeft} size="small" fullWidth>
            <ArrowUpwardIcon sx={{ mr: 1 }} />
            rückw. verschieben
          </Button>
        </Stack>
      )}
      {orientation && (
        <Box width="100%">
          <Label>Spannrichtung</Label>

          <MUITextField
            select
            value={orientation}
            onChange={e => updateOrientationInternal(e.target.value as 'horizontal' | 'vertical')}
            disabled={disableOrientationSelection}
            size="small"
            sx={{ '.MuiStack-root': { marginTop: 0 } }}
            margin="none"
            fullWidth
          >
            <MenuItem value="horizontal">0 grad</MenuItem>
            <MenuItem value="vertical">90 grad</MenuItem>
          </MUITextField>
        </Box>
      )}
      <TextField
        select
        name="storey"
        label="Stockwerk"
        onChange={e => {
          updateStorey(guid, e.target.value)
        }}
        disabled={disableStoreySelection}
        disableError
        data-cy="form-storey-select"
      >
        {orderedStoreys.map(storey => (
          <MenuItem key={storey} value={storey}>
            {storeyToLabel(storey)}
          </MenuItem>
        ))}
      </TextField>

      <FormGroup>
        <SwitchLabeled
          checked={watch('xy_decimal_points_rounding')}
          onChange={e => setValue('xy_decimal_points_rounding', e.target.checked)}
          label="XY auf 2 Dezimalstellen begrenzen"
        />
        <SwitchLabeled
          checked={watch('automatic_z_calculation')}
          onChange={e => setValue('automatic_z_calculation', e.target.checked)}
          label="Z aus Ebene berechnen"
        />
      </FormGroup>
      <Errors />
    </>
  )
}

export default FormFields
